<template>
<div class="header-user">
    <PhUserCircle 
        :size="22" 
        :weight="'regular'" 
        :color="iconColor" 
        class="header-user__icon" 
        :class="{ 'header-user__icon--active': isUserRoute }"
        @click="handleClick"
    />
    <!-- Dropdown (только для авторизованных, только на desktop) -->
    <div 
        v-if="authInfo.viewer !== ANONYMOUS_VIEWER" 
        class="header-user__dropdown"
    >
        <!-- Arrow -->
        <div class="header-user__arrow" aria-hidden="true"></div>

        <!-- Content Card -->
        <div class="header-user__card">
            <div class="header-user__content">
                <!-- Profile Header -->
                <div class="header-user__profile">
                    <p class="header-user__label">Мой профиль</p>
                    <div class="header-user__info">
                        <div class="header-user__avatar">
                            <PhUserCircle :size="22" :weight="'fill'" :color="iconColor" />
                        </div>
                        <div class="header-user__details">
                            <p v-if="authInfo.userName" class="header-user__name">{{ authInfo.userName }}</p>
                            <p class="header-user__email">{{ authInfo.userEmail }}</p>
                            <p class="header-user__region" v-if="userRegionLabel">{{ userRegionLabel }}</p>
                        </div>
                    </div>
                </div>

                <!-- Navigation Links -->
                <nav class="header-user__nav">
                    <router-link to="/user/orders" class="header-user__link">
                        <span>Мои заказы</span>
                        <PhCaretRight :size="16" :weight="'bold'" class="header-user__link-icon" />
                    </router-link>
                    <router-link to="/user/addresses" class="header-user__link">
                        <span>Адреса</span>
                        <PhCaretRight :size="16" :weight="'bold'" class="header-user__link-icon" />
                    </router-link>
                </nav>

                <!-- Logout Button -->
                <button @click="handleLogout" class="header-user__logout">
                    <PhSignOut :size="16" :weight="'fill'" />
                    Выйти из аккаунта
                </button>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import { useAuth } from '@/store/useAuth'
import { PhUserCircle, PhSignOut, PhCaretRight } from '@phosphor-icons/vue'
import { isRouteInList } from '@/utils/routeUtils'
import { ROUTES, ERROR_MESSAGES, ANONYMOUS_VIEWER } from '@/constants'

export default {
    components: {
        PhUserCircle, PhSignOut, PhCaretRight
    },
    setup() {
        const authInfo = useAuth();
        return { authInfo }
    },
    data() {
        return {
            isLogin: localStorage.getItem('isLogin'),
            ANONYMOUS_VIEWER: ANONYMOUS_VIEWER
        }
    },
    computed: {
        isUserRoute() {
            const userRoutes = ['user', 'profile', 'orders', 'addresses', 'login']
            return isRouteInList(this.$route, userRoutes)
        },
        iconColor() {
            return this.isUserRoute ? '#DC2626' : '#475569'
        },
        logoutIconColor() {
            return '#DC2626'
        },
        userRegionLabel() {
            return this.authInfo.region?.name || ''
        }
    },
    methods: {
        handleLogout() {
            if (confirm(ERROR_MESSAGES.AUTH.LOGOUT)) {
                this.authInfo.logOut()
            }
        },
        handleClick() {
            // Если не залогинен - сразу переходим на страницу входа
            if (this.authInfo.viewer === ANONYMOUS_VIEWER) {
                this.$router.push({ path: ROUTES.LOGIN })
            } else {
                // Если залогинен - переходим в личный кабинет
                this.$router.push({ path: '/user/profile' })
            }
        }
    }
}
</script>

<style scoped>
/* Container - фиксированные размеры предотвращают layout shift */
.header-user {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

/* Icon */
.header-user__icon {
    cursor: pointer;
    transition: color 0.2s ease;
    color: var(--slate-600, #475569);
}

.header-user:hover .header-user__icon,
.header-user__icon--active {
    color: var(--primary-main, #DC2626) !important;
}

/* Dropdown - скрыт по умолчанию, появляется на hover (только desktop) */
.header-user__dropdown {
    position: absolute;
    top: 100%;
    right: -0.5rem;
    width: 18rem;
    padding-top: 0.75rem;
    z-index: 50;
    /* Скрываем на мобильных и по умолчанию */
    display: none;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-4px);
    transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s;
}

/* Показываем dropdown на hover (только lg и выше) */
@media (min-width: 1024px) {
    .header-user:hover .header-user__dropdown {
        display: block;
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
    }
}

/* Arrow - позиционирован относительно dropdown */
.header-user__arrow {
    position: absolute;
    top: 0.5rem;
    right: 1rem;
    width: 0.75rem;
    height: 0.75rem;
    background: white;
    border-top: 1px solid var(--slate-200, #e2e8f0);
    border-left: 1px solid var(--slate-200, #e2e8f0);
    transform: rotate(45deg);
    z-index: 1;
}

/* Card */
.header-user__card {
    background: white;
    border: 1px solid var(--slate-200, #e2e8f0);
    border-radius: 1rem;
    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.1), 0 4px 12px -4px rgba(0, 0, 0, 0.05);
    overflow: hidden;
}

.header-user__content {
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

/* Profile Section */
.header-user__profile {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.header-user__label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--slate-600, #475569);
    margin: 0;
}

.header-user__info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.header-user__avatar {
    width: 2.75rem;
    height: 2.75rem;
    background: var(--slate-100, #f1f5f9);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.header-user__details {
    min-width: 0;
    flex: 1;
}

.header-user__name {
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--slate-900, #0f172a);
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.header-user__email {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--slate-600, #475569);
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.header-user__region {
    font-size: 0.75rem;
    color: var(--slate-500, #64748b);
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Navigation */
.header-user__nav {
    background: var(--slate-50, #f8fafc);
    border-radius: 0.75rem;
    overflow: hidden;
}

.header-user__link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.625rem 0.875rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--slate-700, #334155);
    text-decoration: none;
    transition: background-color 0.15s ease;
}

.header-user__link:not(:last-child) {
    border-bottom: 1px solid var(--slate-200, #e2e8f0);
}

.header-user__link:hover {
    background: white;
}

.header-user__link-icon {
    color: var(--slate-400, #94a3b8);
    flex-shrink: 0;
}

/* Logout Button */
.header-user__logout {
    width: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    border-radius: 9999px;
    background: var(--red-50, #fef2f2);
    color: var(--primary-main, #DC2626);
    font-size: 0.875rem;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: background-color 0.15s ease;
}

.header-user__logout:hover {
    background: var(--red-100, #fee2e2);
}
</style>